import React, { ChangeEventHandler, useCallback, useMemo, useRef } from 'react'

import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { useShadowState } from '../../../../lib/hooks/useShadowState'
import { SizeChart } from '../../../../types/graphql'

import { ChartNav, ChartNavWrap, Label, Radio, Span, TabLabel, TabNav } from './styles'

interface IProps {
  mensSizeCharts: SizeChart[]
  womensSizeCharts: SizeChart[]
  selectedGender: string
  selectedChart: SizeChart
  onChartSelected(tabs: string, chart: string | undefined | null): void
}

const tabsNavName = 'tabsNavName'
const chartLabelName = 'chartLabelName'

export const SizeChartNav: React.FC<IProps> = ({
  mensSizeCharts,
  womensSizeCharts,
  selectedGender,
  selectedChart,
  onChartSelected,
}) => {
  const {
    sizesSection: { navigation: strings },
  } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const { shadowState } = useShadowState(scrollRef)

  // Generate the gender tabs
  // `label` is used for presentation, `key` is used as internal identifier for the gender
  const genderTabs = useMemo(
    () => [
      { label: strings.tabs.womens, key: 'womens' },
      { label: strings.tabs.mens, key: 'mens' },
    ],
    [strings.tabs]
  )

  // When the gender tab changes
  const handleTabChange = useCallback(
    (gender: string) => () => {
      onChartSelected(gender, selectedChart.navLabel)
    },
    [selectedChart.navLabel, onChartSelected]
  )

  // Set the appropriate gender & category for selectedChart
  const handleChartChange = useCallback(
    (gender: string): ChangeEventHandler<HTMLInputElement> =>
      evt => {
        const label = evt.target.value
        if (!label) return
        onChartSelected(gender, label)
      },
    [onChartSelected]
  )

  return (
    <>
      <TabNav role='tablist'>
        {genderTabs.map(({ label, key }) => (
          <TabLabel key={key} role='tab' aria-selected={selectedGender === key}>
            <Radio
              name={tabsNavName}
              value={label}
              onChange={handleTabChange(key)}
              checked={selectedGender === key}
            />
            <Span>{label}</Span>
          </TabLabel>
        ))}
      </TabNav>
      <ChartNavWrap
        showLeftShadow={shadowState === 'left' || shadowState === 'both'}
        showRightShadow={shadowState === 'right' || shadowState === 'both'}
      >
        <ChartNav ref={scrollRef}>
          {selectedGender === 'womens' && womensSizeCharts
            ? womensSizeCharts.map(chart => {
                return chart.navLabel ? (
                  <Label key={`womens-${chart.navLabel}`}>
                    <Radio
                      data-testid={`${selectedGender}-${chart.navLabel}`}
                      name={chartLabelName}
                      value={chart.navLabel}
                      onChange={handleChartChange('womens')}
                      checked={selectedChart.navLabel === chart.navLabel}
                    />
                    <span>{chart.navLabel}</span>
                  </Label>
                ) : null
              })
            : selectedGender === 'mens' && mensSizeCharts
            ? mensSizeCharts.map(chart => {
                return chart.navLabel ? (
                  <Label key={`mens-${chart.navLabel}`}>
                    <Radio
                      data-testid={`${selectedGender}-${chart.navLabel}`}
                      name={chartLabelName}
                      value={chart.navLabel}
                      onChange={handleChartChange('mens')}
                      checked={selectedChart.navLabel === chart.navLabel}
                    />
                    <span>{chart.navLabel}</span>
                  </Label>
                ) : null
              })
            : null}
        </ChartNav>
      </ChartNavWrap>
    </>
  )
}
